import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Phone, CreditCard, Building2, User, Hash,
  ShieldCheck, CheckCircle, ArrowRight, RefreshCw, Lock
} from 'lucide-react';

const isValidPhone = (p) => p.replace(/\D/g, '').length >= 9;

const SA_BANKS = [
  { name: 'ABSA Bank',                 code: '632005' },
  { name: 'African Bank',              code: '430000' },
  { name: 'Bidvest Bank',              code: '462005' },
  { name: 'Capitec Bank',              code: '470010' },
  { name: 'Discovery Bank',            code: '679000' },
  { name: 'First National Bank (FNB)', code: '250655' },
  { name: 'Investec Bank',             code: '580105' },
  { name: 'Nedbank',                   code: '198765' },
  { name: 'Standard Bank',            code: '051001' },
  { name: 'TymeBank',                  code: '678910' },
  { name: 'Other',                     code: '' },
];

function StepDot({ num, label, active, done }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors',
        done ? 'border-success bg-success text-background'
          : active ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border text-muted-foreground'
      )}>
        {done ? <CheckCircle size={16} /> : num}
      </div>
      <span className={cn('text-xs font-semibold', active ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
    </div>
  );
}

function FieldLabel({ icon: Icon, label, htmlFor }) {
  return (
    <Label htmlFor={htmlFor} className="mb-2 flex items-center gap-1.5">
      {Icon && <Icon size={13} />} {label}
    </Label>
  );
}

function Onboarding() {
  const navigate = useNavigate();
  const { setNeedsOnboarding, user } = useAuth();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const role = onboardingStatus?.role || user?.role || 'buyer';
  const needsBanking = onboardingStatus?.needs_banking ?? role === 'seller';
  const roleMessage = needsBanking
    ? 'Add payout details to receive released funds.'
    : 'Verify your phone number to protect your TrustTrade account.';

  // Which top-level step: 1=phone, 2=banking, 3=done
  const [step, setStep] = useState(1);

  // Phone sub-steps
  const [phoneSubStep, setPhoneSubStep] = useState('enter'); // 'enter' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const otpRefs = useRef([]);

  // Banking
  const [bankingDetails, setBankingDetails] = useState({
    bank_name: '', account_holder: '', account_number: '',
    branch_code: '', account_type: 'savings',
  });
  const [bankingLoading, setBankingLoading] = useState(false);

  useEffect(() => {
    if (user?.name) {
      setBankingDetails(prev => ({ ...prev, account_holder: prev.account_holder || user.name }));
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    api.get('/auth/onboarding-status')
      .then(res => {
        if (cancelled) return;
        const status = res.data || {};
        setOnboardingStatus(status);

        if (!status.needs_onboarding) {
          setStep(3);
        } else if (!status.has_phone) {
          setStep(1);
        } else if (status.needs_banking && !status.has_banking) {
          setStep(2);
        } else {
          setStep(3);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load onboarding status:', err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  // ── Phone step ──────────────────────────────────────────────────────────────

  const handlePhoneSubmit = async () => {
    if (!isValidPhone(phone)) { toast.error('Enter a valid mobile number'); return; }
    setPhoneLoading(true);
    try {
      const res = await api.post('/auth/phone/submit', { phone });
      setPhone(res.data.phone || phone);
      setResendCooldown(60);
      setPhoneSubStep('otp');
      toast.success('Verification code sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (next.every(d => d) && next.join('').length === 6) handleOtpVerify(next.join(''));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpVerify = async (code = otp.join('')) => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setPhoneLoading(true);
    try {
      await api.post('/auth/phone/verify', { phone, otp_code: code });
      toast.success('Phone verified!');
      setStep(needsBanking ? 2 : 3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect code');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setPhoneLoading(true);
    try {
      await api.post('/auth/phone/resend', { phone });
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
      toast.success('New code sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setPhoneLoading(false);
    }
  };

  // ── Banking step ─────────────────────────────────────────────────────────────

  const handleBankSelect = (name) => {
    const bank = SA_BANKS.find(b => b.name === name);
    setBankingDetails(prev => ({ ...prev, bank_name: name, branch_code: bank?.code || prev.branch_code }));
  };

  const handleBankingSubmit = async (e) => {
    e.preventDefault();
    const { bank_name, account_number, branch_code } = bankingDetails;
    if (!bank_name || !account_number || !branch_code) {
      toast.error('Please fill in all required fields'); return;
    }
    if (account_number.length < 8) { toast.error('Enter a valid account number'); return; }
    setBankingLoading(true);
    try {
      await api.post('/users/banking-details', bankingDetails);
      toast.success('Banking details saved');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save banking details');
    } finally {
      setBankingLoading(false);
    }
  };

  // ── Complete ─────────────────────────────────────────────────────────────────

  const handleComplete = () => {
    setNeedsOnboarding(false);
    navigate('/dashboard', { replace: true });
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="tt-product min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-4 py-6">
      {/* Header */}
      <div className="mb-7 w-full max-w-[520px] text-center">
        <div className="mb-3.5 inline-flex items-center gap-2">
          <ShieldCheck size={20} className="text-primary" />
          <span className="text-lg font-bold tracking-tight">TrustTrade</span>
        </div>
        <h1 className="mb-2 text-2xl font-bold tracking-tight">
          Complete your account setup
        </h1>
        <p className="text-sm text-muted-foreground">
          Verify your identity to start using TrustTrade
        </p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {roleMessage} Phone verification helps protect buyers and sellers from fraud.
        </p>
      </div>

      {/* Step indicator */}
      <div className="mb-6 w-full max-w-[520px]">
        <div className="flex items-start justify-center gap-0">
          <StepDot num={1} label="Phone" active={step === 1} done={step > 1} />
          <div className={cn('mx-2 mt-[15px] h-0.5 max-w-[80px] flex-1 transition-colors', step > 1 ? 'bg-success' : 'bg-border')} />
          {needsBanking && (
            <>
              <StepDot num={2} label="Banking" active={step === 2} done={step > 2} />
              <div className={cn('mx-2 mt-[15px] h-0.5 max-w-[80px] flex-1 transition-colors', step > 2 ? 'bg-success' : 'bg-border')} />
            </>
          )}
          <StepDot num={needsBanking ? 3 : 2} label="Done" active={step === 3} done={false} />
        </div>
      </div>

      {/* ── Step 1: Phone ── */}
      {step === 1 && (
        <Card className="w-full max-w-[520px] p-7">
          {phoneSubStep === 'enter' ? (
            <>
              <div className="mb-5 flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                  <Phone size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-bold">Verify phone number</h2>
                  <p className="text-xs text-muted-foreground">South African mobile number required</p>
                </div>
              </div>

              <FieldLabel icon={Phone} label="Mobile number *" htmlFor="ob-phone" />
              <Input
                id="ob-phone"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/[^\d+\s\-()]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handlePhoneSubmit()}
                placeholder="0821234567"
              />
              <p className="mb-5 mt-1.5 text-xs text-muted-foreground">
                Enter your SA number — we'll add +27 automatically
              </p>

              <Button
                onClick={handlePhoneSubmit}
                disabled={phoneLoading || !isValidPhone(phone)}
                className="w-full"
              >
                {phoneLoading ? 'Sending…' : <><span>Send verification code</span><ArrowRight size={15} /></>}
              </Button>

              <Button
                variant="outline"
                onClick={() => { setNeedsOnboarding(false); navigate('/dashboard', { replace: true }); }}
                className="mt-2.5 w-full"
              >
                Skip for now - verify before payment or release actions
              </Button>
            </>
          ) : (
            <>
              <div className="mb-5 flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full border border-success/30 bg-success/10">
                  <ShieldCheck size={18} className="text-success" />
                </div>
                <div>
                  <h2 className="text-base font-bold">Enter verification code</h2>
                  <p className="text-xs text-muted-foreground">Sent to {phone}</p>
                </div>
              </div>

              <div className="mb-5 flex justify-center gap-2">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className={cn(
                      'h-14 w-12 rounded-md border bg-transparent text-center text-xl font-bold outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring',
                      digit ? 'border-primary' : 'border-input'
                    )}
                  />
                ))}
              </div>

              <Button
                onClick={() => handleOtpVerify()}
                disabled={phoneLoading || otp.join('').length !== 6}
                className="mb-3 w-full"
              >
                {phoneLoading ? 'Verifying…' : 'Verify code'}
              </Button>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => { setPhoneSubStep('enter'); setOtp(['', '', '', '', '', '']); }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Change number
                </button>
                {resendCooldown > 0 ? (
                  <span className="text-xs text-muted-foreground">Resend in {resendCooldown}s</span>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={phoneLoading}
                    className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80"
                  >
                    <RefreshCw size={12} /> Resend code
                  </button>
                )}
              </div>
            </>
          )}
        </Card>
      )}

      {/* ── Step 2: Banking ── */}
      {step === 2 && (
        <Card className="w-full max-w-[520px] p-7">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <CreditCard size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold">Add banking details</h2>
              <p className="text-xs text-muted-foreground">Required to receive payouts</p>
            </div>
          </div>

          <div className="mb-5 flex gap-2.5 rounded-md border border-primary/25 bg-primary/5 p-3">
            <Lock size={13} className="mt-0.5 shrink-0 text-primary" />
            <p className="text-xs text-muted-foreground">
              Stored to enable your payouts and transmitted securely to our payment processor, TradeSafe.
            </p>
          </div>

          <form onSubmit={handleBankingSubmit}>
            <div className="mb-4">
              <FieldLabel icon={Building2} label="Bank name *" htmlFor="ob-bank" />
              <Select value={bankingDetails.bank_name} onValueChange={handleBankSelect} required>
                <SelectTrigger id="ob-bank" className="h-10">
                  <SelectValue placeholder="Select your bank" />
                </SelectTrigger>
                <SelectContent>
                  {SA_BANKS.map(b => (
                    <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-4">
              <FieldLabel icon={User} label="Account holder name *" htmlFor="ob-holder" />
              <Input
                id="ob-holder"
                value={bankingDetails.account_holder}
                onChange={e => setBankingDetails(prev => ({ ...prev, account_holder: e.target.value }))}
                placeholder="Full name as it appears on account"
                required
              />
            </div>

            <div className="mb-4">
              <FieldLabel icon={CreditCard} label="Account number *" htmlFor="ob-account" />
              <Input
                id="ob-account"
                type="text"
                inputMode="numeric"
                value={bankingDetails.account_number}
                onChange={e => setBankingDetails(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                placeholder="Your bank account number"
                required
                className="font-mono"
              />
            </div>

            <div className="mb-4">
              <FieldLabel icon={Hash} label="Branch code *" htmlFor="ob-branch" />
              <Input
                id="ob-branch"
                value={bankingDetails.branch_code}
                onChange={e => setBankingDetails(prev => ({ ...prev, branch_code: e.target.value }))}
                placeholder="6-digit branch code"
                required
                className="font-mono"
              />
              {bankingDetails.bank_name && bankingDetails.bank_name !== 'Other' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Universal branch code auto-filled for {bankingDetails.bank_name}
                </p>
              )}
            </div>

            <div className="mb-5">
              <FieldLabel label="Account type *" />
              <RadioGroup
                value={bankingDetails.account_type}
                onValueChange={v => setBankingDetails(prev => ({ ...prev, account_type: v }))}
                className="grid grid-cols-2 gap-2.5"
              >
                {[{ v: 'savings', l: 'Savings' }, { v: 'current', l: 'Current / Cheque' }].map(({ v, l }) => (
                  <Label
                    key={v}
                    htmlFor={`account-type-${v}`}
                    className={cn(
                      'flex cursor-pointer items-center gap-2.5 rounded-md border px-3.5 py-2.5 font-normal',
                      bankingDetails.account_type === v ? 'border-primary bg-primary/5' : 'border-border'
                    )}
                  >
                    <RadioGroupItem value={v} id={`account-type-${v}`} />
                    <span className="text-sm font-medium">{l}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <Button type="submit" disabled={bankingLoading} className="w-full">
              <ShieldCheck size={15} />
              {bankingLoading ? 'Saving…' : 'Save banking details'}
            </Button>
          </form>
        </Card>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && (
        <Card className="w-full max-w-[520px] p-7 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-success/40 bg-success/15">
            <CheckCircle size={30} className="text-success" />
          </div>
          <h2 className="mb-2.5 text-xl font-bold">
            Account setup complete
          </h2>
          <p className="mb-7 text-sm text-muted-foreground">
            {needsBanking ? "Your phone and banking details are verified. You're ready to receive payouts." : "Your phone number is verified. You're ready to use TrustTrade."}
          </p>
          <Button onClick={handleComplete} className="px-8">
            Go to Dashboard <ArrowRight size={15} />
          </Button>
        </Card>
      )}

      <p className="mt-5 text-center text-xs text-muted-foreground">
        Phone verification is required before payment or release actions
      </p>
    </div>
  );
}

export default Onboarding;
